import React, { useState } from 'react';
import { Calculator, Copy, Trash2, DollarSign } from 'lucide-react';

const CpaCalculatorTool = () => {
  const [totalSpend, setTotalSpend] = useState('');
  const [conversions, setConversions] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const calculate = () => {
    const spend = parseFloat(totalSpend);
    const conv = parseFloat(conversions);

    if (isNaN(spend) || isNaN(conv) || spend < 0 || conv <= 0) {
      setResult(null);
      return;
    }

    // CPA = Total Spend / Number of Conversions
    const cpa = spend / conv;

    setResult({
      totalSpend: spend,
      conversions: conv,
      cpa
    });
  };

  const copyResult = (value) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const clearAll = () => {
    setTotalSpend('');
    setConversions('');
    setResult(null);
  };

  const formatCurrency = (value) => {
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Spend ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={totalSpend}
              onChange={(e) => setTotalSpend(e.target.value)}
              placeholder="e.g., 5000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="total-spend-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Number of Conversions
            </label>
            <input
              type="number"
              value={conversions}
              onChange={(e) => setConversions(e.target.value)}
              placeholder="e.g., 100"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="conversions-input"
            />
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-cpa-btn"
      >
        <Calculator size={18} />
        Calculate CPA
      </button>

      {/* Results Section */}
      {result && (
        <div className="p-4 bg-gray-800 rounded-lg space-y-4" data-testid="cpa-results">
          {/* Main Result */}
          <div className="p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-green-400" />
                <span className="text-sm text-gray-400">Cost Per Acquisition</span>
              </div>
              <button
                onClick={() => copyResult(result.cpa)}
                className={`p-1.5 rounded transition-colors ${
                  copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                }`}
                data-testid="copy-cpa"
              >
                <Copy size={14} />
              </button>
            </div>
            <div className="text-4xl font-bold text-green-400" data-testid="cpa-result">
              {formatCurrency(result.cpa)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              per conversion
            </div>
          </div>

          {/* Summary */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Total Spend</span>
                <span className="text-white font-medium">{formatCurrency(result.totalSpend)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Conversions</span>
                <span className="text-white font-medium">{formatNumber(result.conversions)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                <span className="text-gray-400">Cost Per Acquisition</span>
                <span className="text-green-400 font-medium">{formatCurrency(result.cpa)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formula Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">CPA Formula</h3>
        <div className="text-sm text-gray-400 font-mono bg-gray-900 p-3 rounded">
          CPA = Total Spend / Number of Conversions
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Industry CPA Benchmarks</h3>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div>
            <div className="text-gray-500 mb-1">E-commerce</div>
            <div>$10 - $50</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">SaaS</div>
            <div>$50 - $200</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">B2B Services</div>
            <div>$100 - $500</div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Tips to Lower CPA</h3>
        <div className="text-sm text-gray-400 space-y-1">
          <div>• Improve targeting to reach qualified leads</div>
          <div>• Optimize landing pages for conversion</div>
          <div>• Test different ad creatives and copy</div>
          <div>• Focus on high-performing channels</div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-cpa-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default CpaCalculatorTool;
